import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/LanguageContext';
import LocationDisplay from './LocationDisplay';
import ContactForm from './ContactForm';
import SMSComposer from './SMSComposer';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import WeatherWidget from './WeatherWidget';
import SOSButton from './SOSButton';
import EmergencyHotlines from './EmergencyHotlines';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';

const { FiMapPin, FiRefreshCw, FiUsers, FiMessageSquare, FiInfo } = FiIcons;
const { FaWhatsapp, FaViber, FaTelegram, FaFacebookMessenger } = FaIcons;

const LocationSender = () => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const { t } = useTranslation();

  const getCurrentLocation = () => {
    setLoading(true);
    setError(null);
    if (!navigator.geolocation) {
      setError(t('access_denied'));
      setLoading(false);
      return;
    }

    const options = { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 };
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const timestamp = new Date().toLocaleString();
        setLocation({
          latitude, longitude, accuracy, timestamp,
          googleMapsLink: `https://maps.google.com/?q=${latitude},${longitude}`
        });
        setLoading(false);
      },
      (error) => {
        setError(t('access_denied'));
        setLoading(false);
      },
      options
    );
  };

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const triggerSOS = (type) => {
    if (!location) return;
    const message = `🚨 EMERGENCY SOS! 🚨\n\nI need immediate assistance! My current location:\n📍 Lat: ${location.latitude.toFixed(6)}\n📍 Lon: ${location.longitude.toFixed(6)}\n⏰ Reported at: ${location.timestamp}\n\n🗺️ Live Map Link:\n${location.googleMapsLink}`;
    const encoded = encodeURIComponent(message);
    let url = '';
    
    if (type === 'whatsapp') url = `https://wa.me/?text=${encoded}`;
    else if (type === 'telegram') url = `https://t.me/share/url?text=${encoded}`;
    else if (type === 'viber') url = `viber://forward?text=${encoded}`;
    else if (type === 'sms') url = `sms:?body=${encoded}`;
    else if (type === 'messenger') url = `https://m.me/drrmoiligancity/`;

    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const addContact = (contact) => {
    setContacts([...contacts, { ...contact, id: Date.now() }]);
  };

  const removeContact = (id) => {
    setContacts(contacts.filter(c => c.id !== id));
  };

  return (
    <article className="max-w-6xl mx-auto px-4 py-8 space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* SOS Card */}
        <section className="lg:col-span-2 relative group p-10 bg-indigo-800 rounded-[3rem] shadow-2xl overflow-hidden" aria-labelledby="sos-heading">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-700 via-indigo-800 to-slate-900 z-0">
            <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-white/5 rounded-full blur-[120px] pointer-events-none"></div>
          </div>

          <div className="relative z-10 flex flex-col space-y-10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-10 text-white">
              <div className="space-y-6">
                <header>
                  <h1 id="sos-heading" className="text-4xl font-black tracking-tighter mb-2 italic uppercase">{t('emergency_protocol')}</h1>
                  <div className="h-2 w-24 bg-red-500 rounded-full"></div>
                </header>
                <p className="text-indigo-100 text-lg leading-relaxed max-w-sm font-medium">
                  {t('sos_desc')}
                </p>
                
                <div className="space-y-6">
                  {location && <WeatherWidget lat={location.latitude} lon={location.longitude} />}
                  
                  <div className="space-y-3">
                    <p className="text-[10px] font-black text-indigo-300 uppercase tracking-[0.2em]">{t('broadcast_title')}</p>
                    <div className="flex flex-wrap gap-2.5">
                      <button 
                        onClick={() => triggerSOS('messenger')} 
                        disabled={!location} 
                        className="flex items-center space-x-2 px-4 py-2.5 bg-[#006AFF] text-white rounded-xl hover:bg-[#0052cc] transition-all font-bold text-xs uppercase tracking-wider disabled:opacity-50 shadow-lg"
                      >
                        <SafeIcon icon={FaFacebookMessenger} /> <span>CDRRMO</span>
                      </button>
                      <button 
                        onClick={() => triggerSOS('whatsapp')} 
                        disabled={!location} 
                        className="flex items-center space-x-2 px-4 py-2.5 bg-[#25D366] text-white rounded-xl hover:bg-[#128C7E] transition-all font-bold text-xs uppercase tracking-wider disabled:opacity-50 shadow-lg"
                      >
                        <SafeIcon icon={FaWhatsapp} /> <span>WhatsApp</span>
                      </button>
                      <button 
                        onClick={() => triggerSOS('telegram')} 
                        disabled={!location} 
                        className="flex items-center space-x-2 px-4 py-2.5 bg-[#0088cc] text-white rounded-xl hover:bg-[#0077b5] transition-all font-bold text-xs uppercase tracking-wider disabled:opacity-50 shadow-lg"
                      >
                        <SafeIcon icon={FaTelegram} /> <span>Telegram</span>
                      </button>
                      <button 
                        onClick={() => triggerSOS('viber')} 
                        disabled={!location} 
                        className="flex items-center space-x-2 px-4 py-2.5 bg-[#7360f2] text-white rounded-xl hover:bg-[#5d4dbd] transition-all font-bold text-xs uppercase tracking-wider disabled:opacity-50 shadow-lg"
                      >
                        <SafeIcon icon={FaViber} /> <span>Viber</span>
                      </button>
                      <button 
                        onClick={() => triggerSOS('sms')} 
                        disabled={!location} 
                        className="flex items-center space-x-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all font-bold text-xs uppercase tracking-wider border border-white/10 disabled:opacity-50 shadow-lg"
                      >
                        <SafeIcon icon={FiMessageSquare} /> <span>SMS</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center scale-110">
                <SOSButton onTrigger={triggerSOS} isDisabled={!location} />
              </div>
            </div>

            <aside className="bg-white/10 backdrop-blur-md border border-white/10 rounded-[2rem] p-6 flex flex-col sm:flex-row items-center gap-5">
              <div className="bg-white/10 p-4 rounded-2xl flex-shrink-0">
                <SafeIcon icon={FiInfo} className="text-2xl text-indigo-300" />
              </div>
              <div>
                <h4 className="text-[10px] font-black text-indigo-300 uppercase tracking-[0.2em] mb-1">{t('testing_note_title')}</h4>
                <p className="text-indigo-50 text-xs font-medium leading-relaxed opacity-80">{t('testing_note_desc')}</p>
              </div>
            </aside>
          </div>
        </section>

        {/* GPS Status Card */}
        <section className="bg-white rounded-[3rem] shadow-xl border border-slate-100 p-8 flex flex-col justify-between hover:shadow-2xl transition-all duration-500" aria-label="GPS Status">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-indigo-50 rounded-2xl">
                <SafeIcon icon={FiMapPin} className="text-xl text-indigo-600" />
              </div>
              <h2 className="font-black text-slate-800 uppercase tracking-widest text-xs">{t('gps_status')}</h2>
            </div>
            <button 
              onClick={getCurrentLocation} 
              disabled={loading} 
              className="p-3 hover:bg-slate-50 rounded-2xl transition-all active:rotate-180"
            >
              <SafeIcon icon={FiRefreshCw} className={`${loading ? 'animate-spin text-indigo-600' : 'text-slate-400'} text-lg`} />
            </button>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage message={error} />
          ) : location ? (
            <div className="space-y-6">
              <div className="bg-slate-50 rounded-[2rem] p-6 border border-slate-100 space-y-4">
                <address className="not-italic">
                  <p className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-1.5">{t('precise_coords')}</p>
                  <p className="font-mono text-lg text-slate-900 font-bold tracking-tight">{location.latitude.toFixed(5)}°, {location.longitude.toFixed(5)}°</p>
                </address>
                <div>
                  <p className="text-[10px] uppercase font-black text-slate-400 tracking-widest mb-1.5">{t('signal_accuracy')}</p>
                  <div className="flex items-center space-x-2">
                    <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                    <p className="font-bold text-slate-700">{t('signal_within')} {Math.round(location.accuracy)} {t('meters')}</p>
                  </div>
                </div>
              </div>
              <a 
                href={location.googleMapsLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-3 w-full py-4 bg-slate-950 text-white rounded-2xl font-black text-xs hover:bg-slate-800 transition-all shadow-lg uppercase tracking-widest"
              >
                <SafeIcon icon={FiMapPin} /> <span>{t('open_in_maps')}</span>
              </a>
            </div>
          ) : null}
        </section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <section className="bg-white rounded-[3rem] shadow-xl border border-slate-100 p-10 hover:shadow-2xl transition-shadow duration-500" aria-labelledby="contacts-heading">
          <div className="flex items-center space-x-4 mb-8">
            <div className="p-3 bg-indigo-50 rounded-2xl">
              <SafeIcon icon={FiUsers} className="text-2xl text-indigo-600" />
            </div>
            <h2 id="contacts-heading" className="text-2xl font-black text-slate-900 tracking-tight">{t('trusted_contacts')}</h2>
          </div>
          <ContactForm onAddContact={addContact} contacts={contacts} onRemoveContact={removeContact} />
        </section>

        <section className="bg-white rounded-[3rem] shadow-xl border border-slate-100 p-10 hover:shadow-2xl transition-shadow duration-500" aria-labelledby="comm-heading">
          <div className="flex items-center space-x-4 mb-8">
            <div className="p-3 bg-indigo-50 rounded-2xl">
              <SafeIcon icon={FiMessageSquare} className="text-2xl text-indigo-600" />
            </div>
            <h2 id="comm-heading" className="text-2xl font-black text-slate-900 tracking-tight">{t('comm_hub')}</h2>
          </div>
          {location ? (
            <SMSComposer location={location} contacts={contacts} />
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400 italic text-sm py-20 border-2 border-dashed border-slate-100 rounded-[2.5rem]">
              {t('wait_gps')}
            </div>
          )}
        </section>
      </div>

      <EmergencyHotlines />
    </article>
  );
};

export default LocationSender;